import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Edit2, Search, Eye, Loader2, ChevronLeft,
  Trash2, Send, CreditCard, ArrowLeftRight, Download,
  Calendar, User, Building2, Hash, AlertCircle, Check, X,
  Copy, RefreshCw
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

// ============================================================================
// FACTURATION MODULE
// ============================================================================

const STATUT_CONFIG = {
  brouillon: { label: 'Brouillon', color: '#6b7280', bg: '#f3f4f6' },
  envoyee: { label: 'Envoyée', color: '#3b82f6', bg: '#eff6ff' },
  payee: { label: 'Payée', color: '#10b981', bg: '#d4f4dd' },
  partielle: { label: 'Partielle', color: '#f59e0b', bg: '#fef3c7' },
  en_retard: { label: 'En retard', color: '#ef4444', bg: '#fee2e2' },
  annulee: { label: 'Annulée', color: '#9ca3af', bg: '#f3f4f6' },
};

const LIGNE_VIDE = { designation: '', description: '', quantite: 1, unite: 'unité', prix_unitaire: '', remise_pourcent: 0 };

const FacturationModule = () => {
  // États
  const [factures, setFactures] = useState([]);
  const [totaux, setTotaux] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  // Vues
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail' | 'create'
  const [selectedFacture, setSelectedFacture] = useState(null);

  // Partenaires (pour le sélecteur)
  const [partenaires, setPartenaires] = useState([]);
  const [caisses, setCaisses] = useState([]);

  // Formulaire création
  const [formData, setFormData] = useState({
    type_document: 'facture', partenaire_id: '', client_nom: '',
    client_adresse: '', client_telephone: '', client_email: '', client_nif: '',
    date_emission: new Date().toISOString().split('T')[0],
    date_echeance: '', taux_tva: 0, remise_globale: 0,
    objet: '', notes: '', conditions: '',
  });
  const [lignes, setLignes] = useState([{ ...LIGNE_VIDE }]);
  const [isSaving, setIsSaving] = useState(false);

  // Modal paiement
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [paiementForm, setPaiementForm] = useState({
    montant: '', caisse_id: '', mode_paiement: '', reference: '',
    date_paiement: new Date().toISOString().split('T')[0], notes: '',
  });
  const [isSavingPaiement, setIsSavingPaiement] = useState(false);

  // Confirm
  const [confirmConfig, setConfirmConfig] = useState(null);

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, symbole, getNumber } = useParamsCompta();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  // ============================================================================
  // CHARGEMENT
  // ============================================================================
  useEffect(() => { loadFactures(); loadPartenaires(); loadCaisses(); }, []);

  const loadFactures = async () => {
    showLoader(); setIsLoading(true);
    try {
      const response = await api.facturation.getAll({ type: filterType, statut: filterStatut, search: searchTerm });
      if (response.success) { setFactures(response.data); setTotaux(response.totaux || {}); }
    } catch (error) { console.error('Erreur:', error); }
    finally { setIsLoading(false); hideLoader(); }
  };

  const loadPartenaires = async () => {
    try { const r = await api.partenaires.getAll({}); if (r.success) setPartenaires(r.data); } catch (e) { console.error(e); }
  };

  const loadCaisses = async () => {
    try { const r = await api.tresorerie.getCaisses(); if (r.success) setCaisses(r.data.filter(c => c.statut === 'active')); } catch (e) { console.error(e); }
  };

  useEffect(() => { const t = setTimeout(() => loadFactures(), 300); return () => clearTimeout(t); }, [searchTerm, filterType, filterStatut]);

  // ============================================================================
  // DÉTAIL
  // ============================================================================
  const openDetail = async (facture) => {
    showLoader();
    try {
      const r = await api.facturation.getOne(facture.id);
      if (r.success) { setSelectedFacture(r.data); setCurrentView('detail'); }
    } catch (e) { console.error(e); }
    finally { hideLoader(); }
  };

  // ============================================================================
  // CRÉATION
  // ============================================================================
  const openCreateView = (type = 'facture') => {
    const delaiJours = getNumber('delai_paiement_jours', 30);
    const echeance = new Date();
    echeance.setDate(echeance.getDate() + delaiJours);

    setFormData({
      type_document: type, partenaire_id: '', client_nom: '',
      client_adresse: '', client_telephone: '', client_email: '', client_nif: '',
      date_emission: new Date().toISOString().split('T')[0],
      date_echeance: echeance.toISOString().split('T')[0],
      taux_tva: getNumber('tva_defaut', 0),
      remise_globale: 0, objet: '', notes: '', conditions: '',
    });
    setLignes([{ ...LIGNE_VIDE }]);
    setCurrentView('create');
  };

  const handlePartenaireSelect = (partenaireId) => {
    const p = partenaires.find(x => x.id === parseInt(partenaireId));
    if (p) {
      setFormData({
        ...formData, partenaire_id: p.id,
        client_nom: p.nom, client_adresse: p.adresse || '',
        client_telephone: p.telephone || '', client_email: p.email || '',
        client_nif: p.nif || '',
      });
    } else {
      setFormData({ ...formData, partenaire_id: '' });
    }
  };

  // Lignes
  const addLigne = () => setLignes([...lignes, { ...LIGNE_VIDE }]);
  const removeLigne = (index) => { if (lignes.length > 1) setLignes(lignes.filter((_, i) => i !== index)); };
  const updateLigne = (index, field, value) => {
    const updated = [...lignes];
    updated[index] = { ...updated[index], [field]: value };
    setLignes(updated);
  };

  // Calculs
  const calcLigneMontant = (l) => {
    const brut = (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire) || 0);
    return brut - (brut * (parseFloat(l.remise_pourcent) || 0) / 100);
  };
  const sousTotal = lignes.reduce((s, l) => s + calcLigneMontant(l), 0);
  const remiseGlobale = parseFloat(formData.remise_globale) || 0;
  const montantTva = (sousTotal - remiseGlobale) * (parseFloat(formData.taux_tva) || 0) / 100;
  const montantTotal = sousTotal - remiseGlobale + montantTva;

  const handleSaveFacture = async () => {
    if (lignes.every(l => !l.designation.trim())) { showToast('Ajoutez au moins une ligne', 'error'); return; }
    setIsSaving(true);
    try {
      const response = await api.facturation.creer({ ...formData, lignes: lignes.filter(l => l.designation.trim()) });
      if (response.success) {
        showToast(response.message, 'success');
        setCurrentView('list');
        await loadFactures();
      } else { showToast(response.message || 'Erreur', 'error'); }

   } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
    finally { setIsSaving(false); }
  };

  // ============================================================================
  // ACTIONS
  // ============================================================================
  const handleChangerStatut = async (factureId, statut) => {
    try {
      const r = await api.facturation.changerStatut(factureId, statut);
      if (r.success) { showToast(r.message); await openDetail({ id: factureId }); await loadFactures(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
  };

  const handlePaiement = async () => {
    if (!paiementForm.montant || parseFloat(paiementForm.montant) <= 0) { showToast('Montant invalide', 'error'); return; }
    setIsSavingPaiement(true);
    try {
      const r = await api.facturation.enregistrerPaiement(selectedFacture.id, { ...paiementForm, montant: parseFloat(paiementForm.montant) });
      if (r.success) {
        showToast(r.message); setShowPaiementModal(false);
        await openDetail({ id: selectedFacture.id }); await loadFactures();
      } else showToast(r.message || 'Erreur', 'error');


    } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
    finally { setIsSavingPaiement(false); }
  };
  

  const handleConvertir = async (devisId) => {
    try {
      const r = await api.facturation.convertirDevis(devisId);
      if (r.success) { showToast(r.message); await loadFactures(); }
      else showToast(r.message || 'Erreur', 'error');

  } catch (e) { const msg = e.response?.data?.message || e.message || 'Erreur'; showToast(msg, 'error'); }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  // ============================================================================
  // VUE LISTE
  // ============================================================================
  const renderList = () => (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="text-xs font-semibold text-gray-500 mb-1">Factures</div>
          <div className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{totaux.nb_factures || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="text-xs font-semibold text-gray-500 mb-1">Devis</div>
          <div className="text-2xl font-bold text-amber-600">{totaux.nb_devis || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="text-xs font-semibold text-gray-500 mb-1">CA Total</div>
          <div className="text-xl font-bold text-green-600">{formatMontant(totaux.ca_total)}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #ef4444', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="text-xs font-semibold text-gray-500 mb-1">Impayés</div>
          <div className="text-xl font-bold text-red-600">{formatMontant(totaux.total_impaye)}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => openCreateView('facture')} className="flex-1 rounded-2xl p-4 text-center transition-all hover:shadow-md" style={{ backgroundColor: '#f0f9fb', border: '2px dashed #448696' }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#448696'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#448696'}>
            <Plus size={20} className="mx-auto mb-1" style={{ color: '#448696' }} />
            <div className="text-xs font-bold" style={{ color: '#448696' }}>Facture</div>
          </button>
          <button onClick={() => openCreateView('devis')} className="flex-1 rounded-2xl p-4 text-center transition-all hover:shadow-md" style={{ backgroundColor: '#fffbeb', border: '2px dashed #fbbf24' }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#f59e0b'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#fbbf24'}>
            <Plus size={20} className="mx-auto mb-1 text-amber-600" />
            <div className="text-xs font-bold text-amber-600">Devis</div>
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" placeholder="Rechercher par numéro, client, objet..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all"
              onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium">
            <option value="">Tous types</option>
            <option value="facture">Factures</option>
            <option value="devis">Devis</option>
          </select>
          <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium">
            <option value="">Tous statuts</option>
            {Object.entries(STATUT_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {/* Liste */}
      {isLoading ? null : factures.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">Aucun document</h3>
          <p className="text-sm text-gray-500 mb-6">Créez votre première facture ou devis.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="divide-y divide-gray-100">
            {factures.map((f) => {
              const statut = STATUT_CONFIG[f.statut] || STATUT_CONFIG.brouillon;
              const isDevis = f.type_document === 'devis';
              return (
                <div key={f.id} onClick={() => openDetail(f)} className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer group">
                  <div className="flex items-center gap-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center`} style={{ backgroundColor: isDevis ? '#fef3c7' : '#f0f9fb' }}>
                      <FileText size={20} style={{ color: isDevis ? '#d97706' : '#448696' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold text-gray-900">{f.numero}</span>
                        <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: statut.bg, color: statut.color }}>{statut.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 font-medium">
                        <span>{f.client_nom || f.partenaire_nom || 'Sans client'}</span>
                        <span>{formatDate(f.date_emission)}</span>
                        {f.objet && <span className="truncate max-w-[200px]">{f.objet}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(f.montant_total)}</div>
                      {f.type_document === 'facture' && parseFloat(f.reste_a_payer) > 0 && (
                        <div className="text-xs font-medium text-red-500">Reste: {formatMontant(f.reste_a_payer)}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  // ============================================================================
  // VUE DÉTAIL
  // ============================================================================
  const renderDetail = () => {
    if (!selectedFacture) return null;
    const f = selectedFacture;
    const statut = STATUT_CONFIG[f.statut] || STATUT_CONFIG.brouillon;
    const isDevis = f.type_document === 'devis';

    return (
      <>
        <button onClick={() => { setCurrentView('list'); setSelectedFacture(null); }} className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-5">
          <ChevronLeft size={18} /> Retour à la liste
        </button>

        {/* Header */}
        <div className="bg-white rounded-2xl p-6 mb-6" style={{ borderLeft: `4px solid ${isDevis ? '#f59e0b' : '#448696'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{f.numero}</h2>
                <span className="text-xs px-3 py-1.5 rounded-lg font-semibold" style={{ backgroundColor: statut.bg, color: statut.color }}>{statut.label}</span>
              </div>
              <p className="text-sm text-gray-500">{f.client_nom || 'Sans client'}{f.objet ? ` — ${f.objet}` : ''}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(f.montant_total)}</div>
              {!isDevis && parseFloat(f.reste_a_payer) > 0 && (
                <div className="text-sm font-semibold text-red-500 mt-1">Reste à payer: {formatMontant(f.reste_a_payer)}</div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
            {f.statut === 'brouillon' && (
              <button onClick={() => handleChangerStatut(f.id, 'envoyee')} className="px-4 py-2 rounded-xl text-sm font-semibold transition-all text-white shadow-md" style={{ backgroundColor: '#3b82f6' }}>
                <Send size={14} className="inline mr-2" />Marquer envoyée
              </button>
            )}
            {['envoyee', 'partielle', 'en_retard'].includes(f.statut) && (
              <button onClick={() => { setPaiementForm({ montant: f.reste_a_payer.toString(), caisse_id: '', mode_paiement: '', reference: '', date_paiement: new Date().toISOString().split('T')[0], notes: '' }); setShowPaiementModal(true); }}
                className="px-4 py-2 rounded-xl text-sm font-semibold transition-all text-white shadow-md" style={{ backgroundColor: '#10b981' }}>
                <CreditCard size={14} className="inline mr-2" />Encaisser
              </button>
            )}
            {isDevis && f.statut !== 'annulee' && (
              <button onClick={() => handleConvertir(f.id)} className="px-4 py-2 rounded-xl text-sm font-semibold transition-all text-white shadow-md" style={{ backgroundColor: '#448696' }}>
                <Copy size={14} className="inline mr-2" />Convertir en facture
              </button>
            )}
            {f.statut !== 'annulee' && f.statut !== 'payee' && (
              <button onClick={() => handleChangerStatut(f.id, 'annulee')} className="px-4 py-2 rounded-xl text-sm font-semibold transition-all border-2 border-gray-200 text-gray-600 hover:bg-gray-50">
                <X size={14} className="inline mr-2" />Annuler
              </button>
            )}
           <button onClick={async () => {
              try {
                showToast('Génération du PDF en cours...', 'loading');
                await api.facturation.exportPDF(f.id);
                showToast('PDF téléchargé !', 'success');
              } catch (e) { showToast('Erreur génération PDF', 'error'); }
            }} className="px-4 py-2 rounded-xl text-sm font-semibold transition-all border-2 border-gray-200 text-gray-600 hover:bg-gray-50">
              <Download size={14} className="inline mr-2" />Télécharger PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lignes */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="px-6 py-4 border-b border-gray-100"><h3 className="text-sm font-bold text-gray-800">Lignes</h3></div>
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
                <th className="px-6 py-3 text-left">Désignation</th>
                <th className="px-3 py-3 text-right">Qté</th>
                <th className="px-3 py-3 text-right">P.U.</th>
                <th className="px-3 py-3 text-right">Remise</th>
                <th className="px-6 py-3 text-right">Montant</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {(f.lignes || []).map((l, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-3"><div className="font-semibold text-gray-900">{l.designation}</div>{l.description && <div className="text-xs text-gray-400 mt-0.5">{l.description}</div>}</td>
                    <td className="px-3 py-3 text-right text-gray-700">{l.quantite} {l.unite}</td>
                    <td className="px-3 py-3 text-right text-gray-700">{formatMontant(l.prix_unitaire)}</td>
                    <td className="px-3 py-3 text-right text-gray-500">{l.remise_pourcent > 0 ? `${l.remise_pourcent}%` : '-'}</td>
                    <td className="px-6 py-3 text-right font-bold text-gray-900">{formatMontant(l.montant_ht)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <div className="flex justify-end gap-8 text-sm">
                <div><span className="text-gray-500">Sous-total:</span> <span className="font-bold">{formatMontant(f.sous_total)}</span></div>
                {parseFloat(f.remise_globale) > 0 && <div><span className="text-gray-500">Remise:</span> <span className="font-bold text-red-600">-{formatMontant(f.remise_globale)}</span></div>}
                {parseFloat(f.taux_tva) > 0 && <div><span className="text-gray-500">TVA ({f.taux_tva}%):</span> <span className="font-bold">{formatMontant(f.montant_tva)}</span></div>}
                <div><span className="text-gray-500">Total:</span> <span className="text-lg font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(f.montant_total)}</span></div>
              </div>
            </div>
          </div>

          {/* Info client + paiements */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h3 className="text-sm font-bold text-gray-800 mb-3">Client</h3>
              <div className="space-y-2 text-sm">
                {f.client_nom && <div className="font-semibold text-gray-900">{f.client_nom}</div>}
                {f.client_telephone && <div className="text-gray-500">{f.client_telephone}</div>}
                {f.client_email && <div className="text-gray-500">{f.client_email}</div>}
                {f.client_adresse && <div className="text-gray-500">{f.client_adresse}</div>}
                {f.client_nif && <div className="text-gray-500">NIF: {f.client_nif}</div>}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h3 className="text-sm font-bold text-gray-800 mb-3">Dates</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Émission</span><span className="font-medium">{formatDate(f.date_emission)}</span></div>
                {f.date_echeance && <div className="flex justify-between"><span className="text-gray-500">Échéance</span><span className="font-medium">{formatDate(f.date_echeance)}</span></div>}
              </div>
            </div>
            {f.penalites_retard && f.penalites_retard.montant > 0 && (
              <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
                <h3 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2"><AlertCircle size={16} /> Pénalités de retard</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-red-600">Retard</span><span className="font-bold text-red-700">{f.penalites_retard.jours_retard} jours ({f.penalites_retard.mois_retard} mois)</span></div>
                  <div className="flex justify-between"><span className="text-red-600">Taux</span><span className="font-bold text-red-700">{f.penalites_retard.taux_mensuel}% / mois</span></div>
                  <div className="flex justify-between"><span className="text-red-600">Pénalités</span><span className="font-bold text-red-700">{formatMontant(f.penalites_retard.montant)}</span></div>
                  <div className="flex justify-between pt-2 border-t border-red-200"><span className="font-bold text-red-800">Total dû</span><span className="font-bold text-red-800 text-lg">{formatMontant(f.penalites_retard.total_avec_penalites)}</span></div>
                </div>
              </div>
            )}

            {f.paiements && f.paiements.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <h3 className="text-sm font-bold text-gray-800 mb-3">Paiements</h3>
                <div className="space-y-3">
                  {f.paiements.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-green-50">
                      <div>
                        <div className="text-sm font-bold text-green-700">{formatMontant(p.montant)}</div>
                        <div className="text-xs text-green-600">{formatDate(p.date_paiement)}{p.caisse_nom ? ` • ${p.caisse_nom}` : ''}</div>
                      </div>
                      <Check size={16} className="text-green-600" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // ============================================================================
  // VUE CRÉATION
  // ============================================================================
  const renderCreate = () => {
    const isDevis = formData.type_document === 'devis';
    return (
      <>
        <button onClick={() => setCurrentView('list')} className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-5">
          <ChevronLeft size={18} /> Retour
        </button>

        <div className="bg-white rounded-2xl p-6 mb-6" style={{ borderLeft: `4px solid ${isDevis ? '#f59e0b' : '#448696'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <h2 className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{isDevis ? 'Nouveau devis' : 'Nouvelle facture'}</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Formulaire principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h3 className="text-sm font-bold text-gray-800 mb-4">Client</h3>
              <div className="mb-4">
                <select value={formData.partenaire_id} onChange={(e) => handlePartenaireSelect(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none transition-all"
                  onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}>
                  <option value="">Sélectionner un partenaire ou saisir manuellement...</option>
                  {partenaires.filter(p => p.type_partenaire === 'client' || p.type_partenaire === 'les_deux').map(p => (
                    <option key={p.id} value={p.id}>{p.nom}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="text" placeholder="Nom client *" value={formData.client_nom} onChange={(e) => setFormData({ ...formData, client_nom: e.target.value })}
                  className="px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" />
                <input type="text" placeholder="Téléphone" value={formData.client_telephone} onChange={(e) => setFormData({ ...formData, client_telephone: e.target.value })}
                  className="px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" />
                <input type="text" placeholder="Email" value={formData.client_email} onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  className="px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" />
                <input type="text" placeholder="NIF" value={formData.client_nif} onChange={(e) => setFormData({ ...formData, client_nif: e.target.value })}
                  className="px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" />
              </div>
            </div>

            {/* Lignes */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-800">Lignes</h3>
                <button onClick={addLigne} className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all" style={{ backgroundColor: '#f0f9fb', color: '#448696' }}>
                  <Plus size={14} className="inline mr-1" />Ajouter
                </button>
              </div>

              <div className="space-y-3">
                {lignes.map((l, i) => (
                  <div key={i} className="p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-all">
                    <div className="flex gap-3 mb-2">
                      <input type="text" placeholder="Désignation *" value={l.designation} onChange={(e) => updateLigne(i, 'designation', e.target.value)}
                        className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                      {lignes.length > 1 && (
                        <button onClick={() => removeLigne(i)} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-red-50 transition-all">
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <input type="number" placeholder="Qté" value={l.quantite} onChange={(e) => updateLigne(i, 'quantite', e.target.value)}
                        className="px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                      <input type="number" placeholder="Prix unitaire" value={l.prix_unitaire} onChange={(e) => updateLigne(i, 'prix_unitaire', e.target.value)}
                        className="px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                      <input type="number" placeholder="Remise %" value={l.remise_pourcent} onChange={(e) => updateLigne(i, 'remise_pourcent', e.target.value)}
                        className="px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                      <div className="px-3 py-2.5 bg-gray-50 rounded-lg text-sm font-bold text-right" style={{ color: '#1a1a2e' }}>
                        {formatMontant(calcLigneMontant(l))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h3 className="text-sm font-bold text-gray-800 mb-4">Détails</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Objet</label>
                  <input type="text" value={formData.objet} onChange={(e) => setFormData({ ...formData, objet: e.target.value })} placeholder="Objet du document..."
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">Date</label>
                    <input type="date" value={formData.date_emission} onChange={(e) => setFormData({ ...formData, date_emission: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">Échéance</label>
                    <input type="date" value={formData.date_echeance} onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">TVA %</label>
                    <input type="number" value={formData.taux_tva} onChange={(e) => setFormData({ ...formData, taux_tva: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">Remise globale</label>
                    <input type="number" value={formData.remise_globale} onChange={(e) => setFormData({ ...formData, remise_globale: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="2"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none" />
                </div>
              </div>
            </div>

            {/* Totaux */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h3 className="text-sm font-bold text-gray-800 mb-4">Récapitulatif</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Sous-total</span><span className="font-bold">{formatMontant(sousTotal)}</span></div>
                {remiseGlobale > 0 && <div className="flex justify-between"><span className="text-gray-500">Remise</span><span className="font-bold text-red-600">-{formatMontant(remiseGlobale)}</span></div>}
                {montantTva > 0 && <div className="flex justify-between"><span className="text-gray-500">TVA ({formData.taux_tva}%)</span><span className="font-bold">{formatMontant(montantTva)}</span></div>}
                <div className="flex justify-between pt-3 border-t border-gray-200">
                  <span className="font-bold text-gray-800">Total</span>
                  <span className="text-xl font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(montantTotal)}</span>
                </div>
              </div>
            </div>

            <button onClick={handleSaveFacture} disabled={isSaving}
              className="w-full py-4 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              style={{ backgroundColor: isDevis ? '#f59e0b' : '#448696' }}>
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `Créer le ${isDevis ? 'devis' : 'la facture'}`}
            </button>
          </div>
        </div>
      </>
    );
  };

  // ============================================================================
  // RENDU PRINCIPAL
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {currentView === 'list' && renderList()}
      {currentView === 'detail' && renderDetail()}
      {currentView === 'create' && renderCreate()}

      {/* MODAL PAIEMENT */}
      {showPaiementModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#10b981' }}>
              <CreditCard size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Encaisser un paiement</h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{`Montant (${symbole}) *`}</label>
              <input type="number" value={paiementForm.montant} onChange={(e) => setPaiementForm({ ...paiementForm, montant: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-lg focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#10b981'; e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
              <div className="mt-1 text-xs text-gray-500">Reste à payer: {formatMontant(selectedFacture?.reste_a_payer)}</div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Caisse</label>
              <select value={paiementForm.caisse_id} onChange={(e) => setPaiementForm({ ...paiementForm, caisse_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none">
                <option value="">Aucune (pas de mouvement trésorerie)</option>
                {caisses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mode</label>
                <select value={paiementForm.mode_paiement} onChange={(e) => setPaiementForm({ ...paiementForm, mode_paiement: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none">
                  <option value="">-</option>
                  <option value="especes">Espèces</option>
                  <option value="virement">Virement</option>
                  <option value="cheque">Chèque</option>
                  <option value="mobile_money">Mobile Money</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
                <input type="date" value={paiementForm.date_paiement} onChange={(e) => setPaiementForm({ ...paiementForm, date_paiement: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowPaiementModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handlePaiement} disabled={isSavingPaiement} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg transition-all disabled:opacity-50" style={{ backgroundColor: '#10b981' }}>
                {isSavingPaiement ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Encaisser'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

export default FacturationModule;